import { useQuery } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'

interface LedgerEntry {
  id: string
  amount: { value: string; currency: string }
  description: string | null
  idempotency_key: string
  metadata: Record<string, unknown> | null
  source_type: string
  source_id: string
  ledger_account_id: string
  ledger_account_path: string
  created_at: string
  updated_at: string
}

interface PaginationMetadata {
  total_records: number
  current_page: number
  total_pages: number
  next_page: number | null
  prev_page: number | null
}

interface LedgerEntriesResponse {
  data: LedgerEntry[]
  pagination: PaginationMetadata
}

interface UseGetLedgerEntriesArgs {
  ledgerAccountId?: string
  ledgerAccountQuery?: string
  cutoff?: string
  pagination: PaginationState
  sorting: SortingState
}

export function useGetLedgerEntries ({ ledgerAccountId, ledgerAccountQuery, cutoff, pagination, sorting }: UseGetLedgerEntriesArgs) {
  return useQuery<LedgerEntriesResponse>({
    queryKey: ['ledger-entries', ledgerAccountId, ledgerAccountQuery, cutoff, pagination, sorting],
    queryFn: async () => {
      const params = new URLSearchParams({
        'page[number]': pagination.pageIndex.toString(),
        'page[size]': pagination.pageSize.toString(),
      })

      // Convert sorting state to sort parameter format (e.g., "created_at" or "-created_at")
      if (sorting.length > 0) {
        const sortColumn = sorting[0].id
        const sortPrefix = sorting[0].desc ? '-' : ''
        params.set('sort', `${sortPrefix}${sortColumn}`)
      }

      if (ledgerAccountId) {
        params.set('ledger_account_id', ledgerAccountId)
      }
      if (ledgerAccountQuery) {
        params.set('ledger_account_query', ledgerAccountQuery)
      }
      if (cutoff) {
        params.set('cutoff', cutoff)
      }

      const response = await fetchWithHeaders(`/api/web/admin/ledger_entries?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch ledger entries')
      }
      return response.json()
    },
  })
}
